/** @format */

import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { Button, Drawer, Input, Popconfirm, Tooltip } from "antd";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import styled from "styled-components";
import { TLocation } from "../../../../redux-modules/declarations/maps/locations";
import { TShiftType } from "../../../../redux-modules/declarations/maps/shiftTypes";
import {
  TPersonaSettimana,
  TTurniSettimana,
  TTurnoSettimana,
} from "../../../../redux-modules/declarations/maps/turniSettimana";
import { selectorPermissionList } from "../../../../redux-modules/selectors/selectorsPermissions";
import { hasPermission } from "../../../../utils/permissions/hasPermission";
import { Routes } from "../../../../utils/Routes";
import {
  dataOraEstesa,
  finisceIlGiornoDopo,
  formatOreDaMinuti,
  messaggiAvvisiTurno,
  nomeCompleto,
  ora,
  orarioTurno,
} from "../format";
import { C } from "../styles";
import { minutiTipo, orarioTipo } from "./AssegnazioneRapida";

interface Props {
  persona: TPersonaSettimana;
  turno: TTurnoSettimana;
  data: string;
  oggi: string;
  permessi: TTurniSettimana["permessi"];
  tipiTurno: TShiftType[];
  sedi: TLocation[];
  onChiudi: () => void;
  onElimina: (turnoId: number) => Promise<boolean>;
  onCambiaOrario: (turnoId: number, tipoTurnoId: string, sedeId: number | null) => Promise<boolean>;
  onAccettaCambio: (turnoId: number, tipoTurnoId: string) => Promise<boolean>;
  onRifiutaCambio: (turnoId: number) => Promise<boolean>;
  onSalvaNota: (turnoId: number, nota: string) => Promise<boolean>;
  onEliminaNota: (turnoId: number) => Promise<boolean>;
}

type TModo = null | "orario" | "cambio";

const STATI: Record<string, { classe: string; titolo: string }> = {
  da_svolgere: { classe: "da-svolgere", titolo: "Da svolgere" },
  in_corso: { classe: "in-corso", titolo: "In corso" },
  timbrato: { classe: "timbrato", titolo: "Timbrato" },
  mancata_timbratura: { classe: "anomalia", titolo: "Mancata timbratura" },
  non_lavorativo: { classe: "riposo", titolo: "Giorno non lavorativo" },
};

const testoStato = (turno: TTurnoSettimana, data: string, oggi: string): string => {
  switch (turno.stato) {
    case "timbrato":
      return turno.timbrature
        .map(t => `Entrata ${ora(t.entrata)}, uscita ${ora(t.uscita)}`)
        .join(" · ");
    case "in_corso": {
      const aperta = turno.timbrature.find(t => t.uscita === null);
      return `Entrata alle ${ora(aperta ? aperta.entrata : null)}, uscita prevista alle ${
        turno.ora_fine
      }.`;
    }
    case "mancata_timbratura":
      return data < oggi
        ? "Nessuna presenza collegata a questo turno."
        : `Il turno è iniziato alle ${turno.ora_inizio} e non risulta nessuna entrata.`;
    case "da_svolgere":
      return "Nessuna timbratura.";
    default:
      return turno.nome;
  }
};

/** "lunedì 12 ottobre 2026 alle 09:30, da Mario Rossi"; senza autore il turno l'ha creato il sistema. */
const testoCreazione = (turno: TTurnoSettimana): string => {
  if (!turno.creato_il) return turno.creato_da ? `Da ${turno.creato_da}` : "Non disponibile";
  return `${dataOraEstesa(turno.creato_il)}, ${
    turno.creato_da ? `da ${turno.creato_da}` : "dal sistema"
  }`;
};

const IconaStato = ({ stato }: { stato: TTurnoSettimana["stato"] }): JSX.Element | null => {
  if (stato === "timbrato") return <LockOutlined />;
  if (stato === "in_corso") return <CheckCircleOutlined />;
  if (stato === "mancata_timbratura") return <ExclamationCircleOutlined />;
  if (stato === "da_svolgere") return <ClockCircleOutlined />;
  return null;
};

export const DettaglioTurno = ({
  persona,
  turno,
  data,
  oggi,
  permessi,
  tipiTurno,
  sedi,
  onChiudi,
  onElimina,
  onCambiaOrario,
  onAccettaCambio,
  onRifiutaCambio,
  onSalvaNota,
  onEliminaNota,
}: Props): JSX.Element => {
  // useHistory da "react-router", come il resto dell'app: il Link di react-router-dom usa un'altra
  // copia di react-router (node_modules annidato) e non vede il Router ("outside a <Router>")
  const history = useHistory();
  const [modo, setModo] = useState<TModo>(null);
  const [salvataggio, setSalvataggio] = useState(false);
  // nota del turno: null = non in modifica, stringa = bozza in modifica
  const [bozzaNota, setBozzaNota] = useState<string | null>(null);
  const permissionList = useSelector(selectorPermissionList);

  const stato = STATI[turno.stato] || STATI.da_svolgere;
  const avvisiGiorno = messaggiAvvisiTurno(turno, persona.avvisi_contratto, "giorno");
  const avvisiSettimana = messaggiAvvisiTurno(turno, persona.avvisi_contratto, "settimana");
  const dalDomani = data > oggi;
  const puoCambiareOrario = turno.modificabile && permessi.crea && turno.lavorativo;
  // nei giorni passati serve anche ShiftPlanning/Candeletepastdays
  const puoEliminare =
    turno.modificabile && permessi.elimina && (data >= oggi || permessi.elimina_passato);
  const puoGestireCambio = turno.cambio_richiesto && turno.modificabile && permessi.gestisci_cambi;
  // le note seguono il permesso ShiftPlanning/Update (come la vista precedente) e restano modificabili
  // anche sui turni timbrati; il backend le blocca solo nei mesi bloccati
  const puoGestireNota = hasPermission(permissionList, "ShiftPlanning", "Update");
  const giornoBloccato = !turno.modificabile && turno.timbrature.length === 0;

  useEffect(() => {
    setBozzaNota(null);
  }, [turno.id]);

  const sedeTurno =
    turno.sede || (sedi.find(s => Number(s.id) === turno.sede_id)?.name ?? "Sede abituale");
  const tipiLavorativi = tipiTurno.filter(
    t => minutiTipo(t) > 0 && String(t.id) !== String(turno.tipo_turno_id),
  );

  const esegui = (azione: () => Promise<boolean>, chiudi = true): void => {
    setSalvataggio(true);
    azione().then(ok => {
      setSalvataggio(false);
      if (ok && chiudi) onChiudi();
      if (ok && !chiudi) setModo(null);
    });
  };

  const sceltaOrario = (tipo: TShiftType): void => {
    if (modo === "cambio") esegui(() => onAccettaCambio(turno.id, String(tipo.id)));
    else esegui(() => onCambiaOrario(turno.id, String(tipo.id), turno.sede_id));
  };

  const mostraPresenze =
    turno.stato === "timbrato" ||
    turno.stato === "in_corso" ||
    turno.stato === "mancata_timbratura";

  return (
    <Drawer
      visible
      width={420}
      onClose={onChiudi}
      title={
        <div>
          <div style={{ fontSize: 17, fontWeight: 700 }}>{nomeCompleto(persona)}</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.testoSecondario }}>
            {moment(data).format("dddd D MMMM YYYY")}
          </div>
        </div>
      }
      footer={
        <Piede>
          {!turno.modificabile && turno.motivo_non_modificabile && (
            <p className="motivo">
              <LockOutlined /> {turno.motivo_non_modificabile}
            </p>
          )}
          {puoCambiareOrario &&
            (dalDomani ? (
              <Button type="primary" disabled={salvataggio} onClick={(): void => setModo("orario")}>
                Cambia orario
              </Button>
            ) : (
              <Tooltip
                title={
                  data < oggi
                    ? permessi.crea_passato && permessi.elimina_passato
                      ? "Nei giorni passati l'orario non si cambia: elimina il turno e assegnane un altro."
                      : "Nei giorni passati l'orario non si cambia."
                    : "L'orario si cambia da domani in poi: per oggi elimina il turno e assegnane un altro."
                }
              >
                <Button disabled>Cambia orario</Button>
              </Tooltip>
            ))}
          {puoEliminare && (
            <Popconfirm
              title="Eliminare questo turno?"
              okText="Elimina"
              cancelText="Annulla"
              okButtonProps={{ danger: true }}
              onConfirm={(): void => esegui(() => onElimina(turno.id))}
            >
              <Button danger disabled={salvataggio}>
                Elimina turno
              </Button>
            </Popconfirm>
          )}
          {mostraPresenze && (
            <Button
              onClick={(): void =>
                history.push(
                  `${Routes.presences}?vista=giornata&data=${data}&pannello=${persona.id}`,
                )
              }
            >
              Apri le Presenze
            </Button>
          )}
        </Piede>
      }
    >
      <Corpo>
        <div>
          <div className="orario">
            {turno.lavorativo ? orarioTurno(turno) : turno.nome}
            {turno.lavorativo && finisceIlGiornoDopo(turno) && (
              <span className="giorno-dopo">giorno dopo</span>
            )}
          </div>
          {turno.lavorativo && (
            <div className="durata">
              {formatOreDaMinuti(turno.minuti)} h di lavoro
              {finisceIlGiornoDopo(turno) ? ", turno notturno" : ""}
              {turno.nome !== `${turno.ora_inizio} - ${turno.ora_fine}` ? `, ${turno.nome}` : ""}
            </div>
          )}
        </div>

        {turno.lavorativo && (
          <div className={`stato ${stato.classe}`}>
            <IconaStato stato={turno.stato} />
            <div>
              <strong>{stato.titolo}</strong>
              {testoStato(turno, data, oggi)}
            </div>
          </div>
        )}

        {avvisiGiorno.length + avvisiSettimana.length > 0 && (
          <section>
            <h3>Avvisi del contratto</h3>
            {avvisiGiorno.length > 0 && <h4>Del giorno</h4>}
            {avvisiGiorno.map(a => (
              <p key={a} className="avviso">
                {a}
              </p>
            ))}
            {avvisiSettimana.length > 0 && <h4>Della settimana</h4>}
            {avvisiSettimana.map(a => (
              <p key={a} className="avviso settimana">
                {a}
              </p>
            ))}
            <p className="nota">Gli avvisi informano, non bloccano il salvataggio.</p>
          </section>
        )}

        {turno.cambio_richiesto && (
          <section>
            <h3>Richiesta di cambio turno</h3>
            <p className="citazione">{turno.messaggio_cambio || "Nessun messaggio."}</p>
            {puoGestireCambio && (
              <div className="pulsanti">
                <Button
                  type="primary"
                  disabled={salvataggio}
                  onClick={(): void => setModo("cambio")}
                >
                  Assegna un altro orario
                </Button>
                <Button
                  disabled={salvataggio}
                  onClick={(): void => esegui(() => onRifiutaCambio(turno.id), false)}
                >
                  Rifiuta la richiesta
                </Button>
              </div>
            )}
          </section>
        )}

        {modo && (
          <section>
            <h3>{modo === "cambio" ? "Orario per il cambio turno" : "Nuovo orario"}</h3>
            <div className="tipi">
              {tipiLavorativi.map(t => (
                <button
                  key={t.id}
                  type="button"
                  className="tipo"
                  disabled={salvataggio}
                  onClick={(): void => sceltaOrario(t)}
                >
                  <span>{t.name}</span>
                  <small>
                    {orarioTipo(t)}, {formatOreDaMinuti(minutiTipo(t))} h
                  </small>
                </button>
              ))}
            </div>
            <Button type="link" onClick={(): void => setModo(null)} style={{ paddingLeft: 0 }}>
              Annulla
            </Button>
          </section>
        )}

        {(!!turno.note || (puoGestireNota && !giornoBloccato)) && (
          <section>
            <div className="titolo-sezione">
              <h3>Nota</h3>
              {puoGestireNota && !giornoBloccato && bozzaNota === null && (
                <div className="azioni-nota">
                  <Button
                    type="link"
                    size="small"
                    disabled={salvataggio}
                    onClick={(): void => setBozzaNota(turno.note || "")}
                  >
                    {turno.note ? "Modifica" : "Aggiungi"}
                  </Button>
                  {!!turno.note && (
                    <Popconfirm
                      title="Eliminare la nota del turno?"
                      okText="Elimina"
                      cancelText="Annulla"
                      okButtonProps={{ danger: true }}
                      onConfirm={(): void => esegui(() => onEliminaNota(turno.id), false)}
                    >
                      <Button type="link" size="small" danger disabled={salvataggio}>
                        Elimina
                      </Button>
                    </Popconfirm>
                  )}
                </div>
              )}
            </div>
            {bozzaNota !== null ? (
              <>
                <Input.TextArea
                  autoFocus
                  aria-label="Nota del turno"
                  placeholder="Scrivi una nota per questo turno"
                  value={bozzaNota}
                  maxLength={5000}
                  autoSize={{ minRows: 3, maxRows: 8 }}
                  onChange={(e): void => setBozzaNota(e.target.value)}
                />
                <div className="pulsanti" style={{ marginTop: 8 }}>
                  <Button
                    type="primary"
                    loading={salvataggio}
                    disabled={!bozzaNota.trim()}
                    onClick={(): void =>
                      esegui(() =>
                        onSalvaNota(turno.id, bozzaNota).then(ok => {
                          if (ok) setBozzaNota(null);
                          return ok;
                        }),
                      false,
                      )
                    }
                  >
                    Salva nota
                  </Button>
                  <Button disabled={salvataggio} onClick={(): void => setBozzaNota(null)}>
                    Annulla
                  </Button>
                </div>
              </>
            ) : turno.note ? (
              <p className="citazione nota-turno">{turno.note}</p>
            ) : (
              <p className="nota">Nessuna nota su questo turno.</p>
            )}
          </section>
        )}

        <section>
          <h3>Dettagli</h3>
          <dl>
            <dt>Sede</dt>
            <dd>{sedeTurno}</dd>
            {turno.lavorativo && (
              <>
                <dt>Fine turno</dt>
                <dd>{turno.fine_nascosta ? "Nascosta al dipendente" : "Visibile al dipendente"}</dd>
              </>
            )}
            {turno.evento && (
              <>
                <dt>Evento</dt>
                <dd>{turno.evento}</dd>
              </>
            )}
            <dt>Creato e assegnato</dt>
            <dd>{testoCreazione(turno)}</dd>
            {turno.modificato_il && (
              <>
                <dt>Ultima modifica</dt>
                <dd>{dataOraEstesa(turno.modificato_il)}</dd>
              </>
            )}
          </dl>
        </section>
      </Corpo>
    </Drawer>
  );
};

const Corpo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  color: ${C.testo};

  .orario {
    display: flex;
    align-items: baseline;
    gap: 8px;
    font-size: 30px;
    font-weight: 700;
    line-height: 1.1;
    font-variant-numeric: tabular-nums;
  }
  .giorno-dopo {
    font-size: 14px;
    font-weight: 600;
    color: ${C.testoSecondario};
  }
  .durata {
    margin-top: 4px;
    font-size: 13px;
    font-weight: 600;
    color: ${C.testoSecondario};
  }
  .stato {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 14px;
    border-radius: 10px;
    font-size: 13.5px;
  }
  .stato .anticon {
    margin-top: 3px;
    font-size: 16px;
  }
  .stato strong {
    display: block;
    margin-bottom: 2px;
  }
  .stato.da-svolgere {
    background: ${C.daSvolgere.fondo};
    color: ${C.daSvolgere.testo};
  }
  .stato.in-corso {
    background: ${C.inCorso.fondo};
    color: ${C.inCorso.testo};
  }
  .stato.timbrato {
    background: ${C.timbrato.fondo};
    color: ${C.timbrato.testo};
  }
  .stato.anomalia {
    background: ${C.anomalia.fondo};
    color: ${C.anomalia.testo};
  }
  h3 {
    margin: 0 0 8px;
    font-size: 13px;
    font-weight: 700;
    color: #3a4160;
  }
  h4 {
    margin: 0 0 6px;
    font-size: 12px;
    font-weight: 600;
    color: ${C.testoSecondario};
  }
  .avviso {
    margin: 0 0 6px;
    padding: 8px 10px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    background: ${C.avviso.fondo};
    color: #6e4300;
  }
  .avviso.settimana {
    background: transparent;
    border: 1px solid ${C.avviso.bordo};
  }
  .nota {
    margin: 0;
    font-size: 12.5px;
    color: ${C.testoSecondario};
  }
  .citazione {
    margin: 0 0 10px;
    padding: 10px 12px;
    border-radius: 8px;
    background: ${C.fondoGruppo};
  }
  .nota-turno {
    margin: 0;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    font-size: 13.5px;
  }
  .titolo-sezione {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
  }
  .azioni-nota {
    display: flex;
    gap: 2px;
    margin-top: -4px;
  }
  .azioni-nota .ant-btn-link {
    padding: 0 6px;
    font-weight: 600;
  }
  .pulsanti {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .tipi {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px;
  }
  .tipo {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 8px 10px;
    border: 1px solid ${C.linea};
    border-radius: 8px;
    background: #fff;
    font: inherit;
    font-weight: 700;
    text-align: left;
    cursor: pointer;
  }
  .tipo:hover,
  .tipo:focus-visible {
    border-color: ${C.daSvolgere.hover};
    background: #f4f5fc;
  }
  .tipo small {
    font-weight: 500;
    color: ${C.testoSecondario};
  }
  dl {
    display: grid;
    grid-template-columns: 120px 1fr;
    gap: 8px 12px;
    margin: 0;
    font-size: 13.5px;
  }
  dt {
    color: ${C.testoSecondario};
  }
  dd {
    margin: 0;
    font-weight: 700;
  }
`;

const Piede = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;

  .motivo {
    flex-basis: 100%;
    margin: 0;
    font-size: 12.5px;
    font-weight: 600;
    color: #4a5170;
  }
`;
